/**
 * Conversions between a `Date` and the string a native `datetime-local` input
 * uses.
 *
 * The input speaks local wall-clock time with no zone, which is exactly what
 * this app wants: a trainee records the time they saw on the clock. Everything
 * here therefore uses the local `Date` accessors deliberately, never UTC.
 *
 * Kept as pure functions because the day/night and weekend derivations in
 * `packages/shared` read the same instants, and a one-hour error across a
 * British Summer Time boundary would misreport a trainee's out-of-hours work.
 */

function pad(value: number, width = 2): string {
  return String(value).padStart(width, '0');
}

/** `YYYY-MM-DDTHH:mm` in local time. Seconds are dropped; the input has none. */
export function toDateTimeLocalValue(date: Date): string {
  return [
    pad(date.getFullYear(), 4),
    '-',
    pad(date.getMonth() + 1),
    '-',
    pad(date.getDate()),
    'T',
    pad(date.getHours()),
    ':',
    pad(date.getMinutes()),
  ].join('');
}

const DATE_TIME_LOCAL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

/**
 * Parses what the input gives back, in local time. Returns null for anything
 * that is not a real moment — an incomplete value while the user is still
 * typing, or a date that does not exist such as 31 February.
 *
 * The round-trip check at the end is what catches the second case: `Date` would
 * silently roll 31 February forward to 3 March, and an entry quietly dated
 * three days late is worse than one the form refuses to accept.
 */
export function fromDateTimeLocalValue(value: string): Date | null {
  const match = DATE_TIME_LOCAL.exec(value);
  if (match === null) return null;

  const [year, month, day, hour, minute] = match.slice(1).map(Number);
  if (
    year === undefined ||
    month === undefined ||
    day === undefined ||
    hour === undefined ||
    minute === undefined
  ) {
    return null;
  }

  const date = new Date(year, month - 1, day, hour, minute, 0, 0);
  if (Number.isNaN(date.getTime())) return null;

  return toDateTimeLocalValue(date) === value ? date : null;
}

/* -------------------------------------------------------------- date only */

/**
 * The same conversions for a native `date` input, which has no time part.
 *
 * Used by the EDT entry's optional end-of-period field. The end of a period is
 * a day, not a moment — "work done between May and August" has no clock time —
 * so the value is pinned to the **end** of the chosen day. Pinning it to
 * midnight instead would make a single-day span end before it started once the
 * start carried any time at all.
 */
export function toDateValue(date: Date): string {
  return [pad(date.getFullYear(), 4), '-', pad(date.getMonth() + 1), '-', pad(date.getDate())].join(
    '',
  );
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

export function fromDateValue(value: string): Date | null {
  const match = DATE_ONLY.exec(value);
  if (match === null) return null;

  const [year, month, day] = match.slice(1).map(Number);
  if (year === undefined || month === undefined || day === undefined) return null;

  const date = new Date(year, month - 1, day, 23, 59, 59, 999);

  // Same round-trip guard as the datetime version: `Date` rolls 31 February
  // forward to 3 March rather than refusing it.
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}
